import axios from 'axios';

// Get API URL from environment variable only
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// // console.log('API Base URL:', BASE_URL);


// // console.log("BASE_URL frontend:",BASE_URL);
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 1800000, // 30 minutes for large uploads
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor with better error logging
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = token;
    }
    // Commented multi-line console.log);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor with better error handling
api.interceptors.response.use(
  (response) => {
    // Commented multi-line console.log);
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired)
    if (error.response && error.response.status === 401) {
      // Check if this is a login endpoint - don't redirect for login errors
      const isLoginEndpoint = error.config?.url?.includes('/user_login');

      if (isLoginEndpoint) {
        // console.log('401 error on login endpoint - letting page handle it');
        return Promise.reject(error);
      }

      // console.log('Token expired, redirecting to login...');

      // Clear any stored tokens
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      // Clear any other auth-related data
      sessionStorage.clear();

      // Redirect to login page
      // Check if we're in a browser environment
      if (typeof window !== 'undefined') {
        // Get current domain for redirect
        const currentDomain = window.location.hostname;
        const protocol = window.location.protocol;
        const port = window.location.port ? `:${window.location.port}` : '';

        // Construct login URL based on current domain
        let loginUrl;
        if (currentDomain === 'localhost' || currentDomain.includes('localhost')) {
          loginUrl = `${protocol}//${currentDomain}${port}/login`;
        } else {
          // For production, redirect to the main domain login
          loginUrl = `${protocol}//${currentDomain}${port}/login`;
        }

        // console.log('Redirecting to:', loginUrl);
        window.location.href = loginUrl;
      }
    }
    
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout:', {
        url: error.config.url,
        method: error.config.method,
        timeout: error.config.timeout
      });
    } else if (!error.response) {
      console.error('Network error:', {
        url: error.config?.url,
        message: 'No response received from server',
        error: error.message
      });
    } else {
      console.error('API error:', {
        url: error.config.url,
        status: error.response.status,
        data: error.response.data
      });
    }
    return Promise.reject(error);
  }
);

class ApiController {
  static async get(endpoint, params = {}) {
    try {
      const response = await api.get(endpoint, { params });
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async post(endpoint, data = {}) {
    // console.log("POST request to:",endpoint);
    // console.log("Data being sent:", data);
    try {
      const response = await api.post(endpoint, data);
      // console.log("POST request response:", response);
      return response.data;
    } catch (error) {
      // Don't log expected errors like 404 (post not found) or course not found
      if (!(error.response && error.response.status === 404 && 
            (error.response.data?.error_msg === 'Post not found' || 
             error.response.data?.message === 'Course not found or inactive'))) {
        console.error("POST request error:", error);
      }
      ApiController.handleError(error);
      throw error;
    }
  }

  static async put(endpoint, data = {}) {
    try {
      const response = await api.put(endpoint, data);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async patch(endpoint, data = {}) {
    try {
      const response = await api.patch(endpoint, data);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async delete(endpoint) {
    try {
      const response = await api.delete(endpoint);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async uploadFile(endpoint, formData, config = {}) {
    try {
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1800000,
        ...config
      });
      return response.data;
    } catch (error) {
      console.error('Upload failed:', error);
      ApiController.handleError(error);
      throw error;
    }
  }

  static async uploadFileWithMethod(endpoint, formData, method = 'POST', config = {}) {
    try {
      const axiosMethod = method.toLowerCase() === 'put' ? api.put :
                         method.toLowerCase() === 'patch' ? api.patch :
                         api.post;

      const response = await axiosMethod(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1800000,
        ...config
      });
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async uploadMultipleFiles(endpoint, files, additionalData = {}, onProgress = null) {
    try {
      const formData = new FormData();

      files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });

      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1800000, // 30 minutes for file uploads
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percentCompleted);
            }
          : undefined,
      });

      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static handleError(error) {
    // Don't log expected errors like 404 (post not found) or course not found to reduce console noise
    if (error.response && error.response.status === 404 && 
        (error.response.data?.error_msg === 'Post not found' || 
         error.response.data?.message === 'Course not found or inactive')) {
      // This is an expected error, don't log it
      return;
    }
    
    // Don't log 401 errors as they're handled in the interceptor
    if (error.response && error.response.status === 401) {
      return;
    }
    
    if (error.response) {
      console.error('Response Error:', error.response.data);
      console.error('Status:', error.response.status);
      console.error('Headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request Error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
  }
}

export const GET = ApiController.get;
export const POST = ApiController.post;
export const PUT = ApiController.put;
export const PATCH = ApiController.patch;
export const DELETE = ApiController.delete;
export const UPLOAD_FILE = ApiController.uploadFile;
export const UPLOAD_FILE_WITH_METHOD = ApiController.uploadFileWithMethod;
export const UPLOAD_MULTIPLE_FILES = ApiController.uploadMultipleFiles;

export default ApiController;
